import { useCallback, useEffect, useRef, useState } from 'react';
import { prepareZXingModule, readBarcodes } from 'zxing-wasm/reader';
import zxingReaderWasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';
import { findCameraWithTorch } from '../helpers';

prepareZXingModule({
  overrides: {
    locateFile: (path: string, prefix: string) =>
      path.endsWith('.wasm') ? zxingReaderWasmUrl : prefix + path,
  },
});

type AdvancedCameraConstraint = MediaTrackConstraintSet & { torch?: boolean };
type ExtendedCameraCapabilities = MediaTrackCapabilities & { torch?: boolean };
type Canvas2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

export interface UseBarcodeScannerOptions {
  onDetected: (ean: string) => void | Promise<void>;
  onReleaseRequest?: () => void;
  restartOnPopstatePath?: string;
}

export interface UseBarcodeScannerResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isCameraActive: boolean;
  isScanning: boolean;
  isSearchingCamera: boolean;
  isTorchSupported: boolean;
  isTorchOn: boolean;
  error: string | null;
  toggleTorch: () => Promise<void>;
  stop: () => Promise<void>;
}

const createCanvas = (width: number, height: number): OffscreenCanvas | HTMLCanvasElement => {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(width, height);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const applyTrackConstraints = async (
  track: MediaStreamTrack,
  advanced: AdvancedCameraConstraint
) => {
  await track.applyConstraints({ advanced: [advanced] } as MediaTrackConstraints);
};

export const useBarcodeScanner = (options: UseBarcodeScannerOptions): UseBarcodeScannerResult => {
  const { onDetected, onReleaseRequest, restartOnPopstatePath } = options;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<OffscreenCanvas | HTMLCanvasElement | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  const stoppedRef = useRef(false);
  const startingRef = useRef(false);
  const selectedCameraIdRef = useRef<string>('');
  const onDetectedRef = useRef(onDetected);
  const onReleaseRequestRef = useRef(onReleaseRequest);
  const isCameraActiveRef = useRef(false);
  const isTorchOnRef = useRef(false);

  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isSearchingCamera, setIsSearchingCamera] = useState(false);
  const [isTorchSupported, setIsTorchSupported] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);
  useEffect(() => {
    onReleaseRequestRef.current = onReleaseRequest;
  }, [onReleaseRequest]);
  useEffect(() => {
    isCameraActiveRef.current = isCameraActive;
  }, [isCameraActive]);
  useEffect(() => {
    isTorchOnRef.current = isTorchOn;
  }, [isTorchOn]);

  const getTrack = (): MediaStreamTrack | null => streamRef.current?.getVideoTracks()[0] ?? null;

  const scanFrame = useCallback(async () => {
    const schedule = () => {
      if (!stoppedRef.current) {
        rafIdRef.current = requestAnimationFrame(scanFrame);
      }
    };

    if (stoppedRef.current) return;

    const video = videoRef.current;
    if (!video || video.readyState < 2 || video.videoWidth === 0) {
      schedule();
      return;
    }

    if (pausedRef.current) {
      schedule();
      return;
    }

    const w = video.videoWidth;
    const h = video.videoHeight;

    if (!canvasRef.current || canvasRef.current.width !== w || canvasRef.current.height !== h) {
      canvasRef.current = createCanvas(w, h);
    }

    const ctx = canvasRef.current.getContext('2d') as Canvas2D | null;
    if (!ctx) {
      schedule();
      return;
    }

    ctx.drawImage(video, 0, 0, w, h);
    const imageData = ctx.getImageData(0, 0, w, h);

    try {
      const results = await readBarcodes(imageData, {
        tryHarder: true,
        formats: ['EAN13', 'EAN8'],
        maxNumberOfSymbols: 1,
      });
      if (results.length > 0 && !stoppedRef.current && !pausedRef.current && results[0].text) {
        pausedRef.current = true;
        try {
          await onDetectedRef.current(results[0].text);
        } finally {
          pausedRef.current = false;
        }
      }
    } catch {
      // per-frame failures are expected; keep scanning
    }

    schedule();
  }, []);

  const applyCameraFeatures = (track: MediaStreamTrack) => {
    const caps = (track.getCapabilities?.() ?? {}) as ExtendedCameraCapabilities;
    setIsTorchSupported(!!caps.torch);
  };

  const stop = useCallback(async () => {
    stoppedRef.current = true;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const track = getTrack();
    if (track && isTorchOnRef.current) {
      try {
        await applyTrackConstraints(track, { torch: false });
      } catch {
        // ignore — track may already be stopped
      }
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    canvasRef.current = null;
    pausedRef.current = false;
    setIsCameraActive(false);
    setIsTorchOn(false);
    setIsTorchSupported(false);
  }, []);

  const start = useCallback(async () => {
    if (startingRef.current || streamRef.current) return;

    startingRef.current = true;
    stoppedRef.current = false;
    setError(null);
    setIsScanning(true);
    setIsSearchingCamera(true);

    try {
      await new Promise((r) => setTimeout(r, 0));
      if (stoppedRef.current) return;

      let selectedId = selectedCameraIdRef.current;
      if (!selectedId) {
        const best = await findCameraWithTorch();
        selectedId = best ?? '';
      }

      const videoConstraints: MediaTrackConstraints = selectedId
        ? {
            deviceId: { exact: selectedId },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          }
        : {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          };

      const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints });
      if (stoppedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;

      const track = stream.getVideoTracks()[0];
      if (!selectedId) {
        selectedCameraIdRef.current = track.getSettings().deviceId ?? '';
      }

      const video = videoRef.current;
      if (!video) {
        throw new Error('Video element not mounted');
      }

      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.playsInline = true;
      video.muted = true;
      await video.play();

      applyCameraFeatures(track);

      setIsCameraActive(true);
      setIsScanning(false);
      setIsSearchingCamera(false);

      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(scanFrame);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : typeof err === 'string' ? err : 'Unknown error';
      console.error('useBarcodeScanner: start failed', err);
      setError(`Unable to start scanner: ${message}`);
      setIsCameraActive(false);
      setIsScanning(false);
      setIsSearchingCamera(false);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } finally {
      startingRef.current = false;
    }
  }, [scanFrame]);

  const toggleTorch = useCallback(async () => {
    const track = getTrack();
    if (!track || !isCameraActiveRef.current) return;

    const nextValue = !isTorchOnRef.current;
    try {
      await applyTrackConstraints(track, { torch: nextValue });
      setIsTorchOn(nextValue);
    } catch (err) {
      console.error('Error toggling torch:', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      await new Promise((r) => setTimeout(r, 0));
      if (!cancelled) await start();
    };
    init();

    const handleUnload = () => {
      stop();
    };

    const handlePopState = () => {
      if (isCameraActiveRef.current) {
        stop();
      } else if (restartOnPopstatePath && window.location.pathname === restartOnPopstatePath) {
        start();
      } else {
        stop();
      }
    };

    const handleMessage = (e: MessageEvent) => {
      if (e.data?.action === 'release_camera_and_close_modal') {
        onReleaseRequestRef.current?.();
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('message', handleMessage);

    return () => {
      cancelled = true;
      stop();
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    videoRef,
    isCameraActive,
    isScanning,
    isSearchingCamera,
    isTorchSupported,
    isTorchOn,
    error,
    toggleTorch,
    stop,
  };
};
